import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Mic, Loader2, Sparkles, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  analyzeSamplesAdHoc,
  getVoiceProfile,
  listTutorVoiceSamples,
} from "@/lib/voice.functions";
import type { VoiceProfile } from "@/lib/voice-style";

type Props = {
  // Notifies parent of the resolved profile (or null if voice matching is off)
  onProfileChange: (profile: VoiceProfile | null) => void;
};

type Mode = "off" | "saved" | "tutor" | "adhoc";

type TutorSample = {
  id: string;
  title: string;
  created_at: string;
  sample: string;
};

export function VoiceMatchPanel({ onProfileChange }: Props) {
  const fetchProfile = useServerFn(getVoiceProfile);
  const analyzeAdHoc = useServerFn(analyzeSamplesAdHoc);
  const fetchTutorSamples = useServerFn(listTutorVoiceSamples);

  const [savedProfile, setSavedProfile] = useState<VoiceProfile | null>(null);
  const [savedEnabled, setSavedEnabled] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(true);

  const [mode, setMode] = useState<Mode>("off");
  const [adhocSample, setAdhocSample] = useState("");
  const [adhocProfile, setAdhocProfile] = useState<VoiceProfile | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Tutor samples
  const [tutorSamples, setTutorSamples] = useState<TutorSample[] | null>(null);
  const [loadingTutor, setLoadingTutor] = useState(false);
  const [selectedTutorId, setSelectedTutorId] = useState<string>("");
  const [tutorProfile, setTutorProfile] = useState<VoiceProfile | null>(null);

  // Load saved profile on mount
  useEffect(() => {
    fetchProfile()
      .then((res) => {
        if (res.exists && res.profile) {
          setSavedProfile(res.profile);
          setSavedEnabled(res.enabled);
          setHasSaved(true);
          if (res.enabled) setMode("saved");
        }
      })
      .catch(() => {})
      .finally(() => setLoadingSaved(false));
  }, [fetchProfile]);

  // Lazy-load tutor samples the first time the user opens the tutor mode
  useEffect(() => {
    if (mode !== "tutor" || tutorSamples !== null || loadingTutor) return;
    setLoadingTutor(true);
    fetchTutorSamples()
      .then((rows) => setTutorSamples(rows))
      .catch((err) => {
        toast.error((err as Error).message);
        setTutorSamples([]);
      })
      .finally(() => setLoadingTutor(false));
  }, [mode, tutorSamples, loadingTutor, fetchTutorSamples]);

  // Propagate the active profile up
  useEffect(() => {
    if (mode === "off") onProfileChange(null);
    else if (mode === "saved") onProfileChange(savedEnabled ? savedProfile : null);
    else if (mode === "adhoc") onProfileChange(adhocProfile);
    else if (mode === "tutor") onProfileChange(tutorProfile);
  }, [mode, savedProfile, savedEnabled, adhocProfile, tutorProfile, onProfileChange]);

  async function handleAnalyzeAdHoc() {
    if (adhocSample.trim().length < 80) {
      toast.error("Paste at least ~80 characters of your own writing.");
      return;
    }
    setAnalyzing(true);
    try {
      const { profile } = await analyzeAdHoc({ data: { samples: [adhocSample.trim()] } });
      setAdhocProfile(profile);
      toast.success("Voice matched for this essay.");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setAnalyzing(false);
    }
  }

  async function handleAnalyzeTutor() {
    const picked = tutorSamples?.find((s) => s.id === selectedTutorId);
    if (!picked) {
      toast.error("Pick a tutor session first.");
      return;
    }
    setAnalyzing(true);
    try {
      const { profile } = await analyzeAdHoc({ data: { samples: [picked.sample] } });
      setTutorProfile(profile);
      toast.success("Voice matched from your tutor work.");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setAnalyzing(false);
    }
  }

  if (loadingSaved) {
    return (
      <div className="rounded-lg border border-border bg-card/50 p-3 text-sm text-muted-foreground">
        <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> Loading voice settings…
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card/60 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Mic className="mt-0.5 h-4 w-4 text-primary" />
          <div>
            <div className="text-sm font-medium">Match my writing voice</div>
            <p className="text-xs text-muted-foreground">
              Make the essay sound like something you'd actually write.
            </p>
          </div>
        </div>
        <Switch
          checked={mode !== "off"}
          onCheckedChange={(on) => {
            if (!on) setMode("off");
            else setMode(hasSaved && savedEnabled ? "saved" : "tutor");
          }}
          aria-label="Toggle voice matching"
        />
      </div>

      {mode !== "off" && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {hasSaved && (
              <button
                type="button"
                onClick={() => setMode("saved")}
                className={`rounded-full px-3 py-1 transition-colors ${
                  mode === "saved"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/70"
                }`}
              >
                <Sparkles className="mr-1 inline h-3 w-3" />
                Saved voice
              </button>
            )}
            <button
              type="button"
              onClick={() => setMode("tutor")}
              className={`rounded-full px-3 py-1 transition-colors ${
                mode === "tutor"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/70"
              }`}
            >
              <GraduationCap className="mr-1 inline h-3 w-3" />
              From a past tutor essay
            </button>
            <button
              type="button"
              onClick={() => setMode("adhoc")}
              className={`rounded-full px-3 py-1 transition-colors ${
                mode === "adhoc"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/70"
              }`}
            >
              Paste a sample
            </button>
          </div>

          {mode === "saved" && savedProfile && (
            <p className="rounded-md bg-primary/5 p-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Active profile: </span>
              {savedProfile.summary}
            </p>
          )}

          {mode === "saved" && !savedProfile && (
            <p className="rounded-md bg-amber-100 p-2 text-xs text-amber-900">
              No saved profile yet.{" "}
              <Link to="/account" hash="voice" className="underline font-medium">
                Set one up in Account → My Voice
              </Link>
              , or use a tutor essay or paste a sample below.
            </p>
          )}

          {mode === "tutor" && (
            <div className="space-y-2">
              <Label className="text-xs">
                Reuse what you actually wrote in tutor mode:
              </Label>
              {loadingTutor ? (
                <p className="text-xs text-muted-foreground">
                  <Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" /> Looking for your tutor essays…
                </p>
              ) : (tutorSamples?.length ?? 0) === 0 ? (
                <p className="rounded-md bg-amber-100 p-2 text-xs text-amber-900">
                  No past tutor essays found yet.{" "}
                  <Link to="/tutor" className="underline font-medium">
                    Write one in the tutor
                  </Link>
                  , or paste a sample instead.
                </p>
              ) : (
                <>
                  <Select
                    value={selectedTutorId}
                    onValueChange={(v) => {
                      setSelectedTutorId(v);
                      if (tutorProfile) setTutorProfile(null);
                    }}
                  >
                    <SelectTrigger className="text-xs">
                      <SelectValue placeholder="Pick a past tutor essay…" />
                    </SelectTrigger>
                    <SelectContent>
                      {tutorSamples!.map((s) => {
                        const date = new Date(s.created_at).toLocaleDateString();
                        const words = s.sample.split(/\s+/).filter(Boolean).length;
                        const title = s.title.length > 60 ? s.title.slice(0, 60) + "…" : s.title;
                        return (
                          <SelectItem key={s.id} value={s.id}>
                            {title} · {words} words · {date}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {selectedTutorId && (
                    <p className="max-h-20 overflow-hidden rounded-md bg-muted/40 p-2 text-[11px] italic leading-relaxed text-muted-foreground">
                      {tutorSamples!
                        .find((s) => s.id === selectedTutorId)
                        ?.sample.slice(0, 280)}
                      …
                    </p>
                  )}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground">
                      {tutorProfile ? "voice analyzed ✓" : "Analyze before generating."}
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleAnalyzeTutor}
                      disabled={analyzing || !selectedTutorId}
                    >
                      {analyzing ? (
                        <>
                          <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> Analyzing…
                        </>
                      ) : tutorProfile ? (
                        "Re-analyze"
                      ) : (
                        "Analyze my voice"
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {mode === "adhoc" && (
            <div className="space-y-2">
              <Label htmlFor="adhoc-sample" className="text-xs">
                Paste ~100–500 words of your own writing (just for this essay):
              </Label>
              <Textarea
                id="adhoc-sample"
                value={adhocSample}
                onChange={(e) => {
                  setAdhocSample(e.target.value);
                  if (adhocProfile) setAdhocProfile(null);
                }}
                placeholder="A past essay, journal entry, long email, or anything you wrote yourself…"
                className="min-h-[120px] text-xs"
                maxLength={8000}
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  {adhocSample.trim().length} chars{" "}
                  {adhocSample.trim().length >= 80 ? "✓" : "(need ~80+)"}
                  {adhocProfile && " · voice analyzed ✓"}
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAnalyzeAdHoc}
                  disabled={analyzing || adhocSample.trim().length < 80}
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> Analyzing…
                    </>
                  ) : adhocProfile ? (
                    "Re-analyze"
                  ) : (
                    "Analyze my voice"
                  )}
                </Button>
              </div>
              {!adhocProfile && adhocSample.trim().length >= 80 && (
                <p className="text-xs text-amber-700">
                  Click "Analyze my voice" before generating, or voice matching will be skipped.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
